import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthModule';
import { useMusicPlayer } from './MusicPlayerContext';
import ChatTopBar from './ChatTopBar';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ChatInlinePlayer from './ChatInlinePlayer';
import { parseSlashCommand, executeSlashCommand } from './slashCommands';
import { buildKuiskaaCandidates } from './chatKuiskaaHelpers';

const API = process.env.REACT_APP_API_URL || '';

function parseJsonResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    return res.text().then((text) => {
      throw new Error(
        text.startsWith('<!')
          ? 'Backend returned HTML. Is the API server running? In development, set REACT_APP_API_URL to your API base URL (see PageContent.js).'
          : text
      );
    });
  }
  return res.json();
}

function formatEventDay(eventDate) {
  if (!eventDate || !/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) return { base: '—', label: null };
  const [y, m, d] = eventDate.split('-').map((x) => parseInt(x, 10));
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const now = new Date();
  const currentYear = now.getFullYear();
  let base = `${dd}. ${mm}.`;
  if (y !== currentYear) base += ` ${y}`;
  const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.getFullYear() + '-' + String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + String(tomorrow.getDate()).padStart(2, '0');
  const label = eventDate === todayStr ? 'Tänään' : eventDate === tomorrowStr ? 'Huomenna' : null;
  return { base, label };
}

function getEventStartTime(ev) {
  const [hh, mm = 0] = (ev.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  if (ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
    const [y, m, day] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
    d.setFullYear(y, m - 1, day);
  }
  d.setHours(hh, mm, 0, 0);
  return d;
}

function isEventUpcoming(ev) {
  return getEventStartTime(ev).getTime() > Date.now();
}

function getEventEndTime(ev) {
  const start = getEventStartTime(ev);
  const durationMinutes = ev.minutes != null ? Math.max(ev.minutes, 60) : 60;
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

function getNowLiveEvent(events) {
  const now = Date.now();
  for (const ev of events) {
    const start = getEventStartTime(ev).getTime();
    const end = getEventEndTime(ev).getTime();
    if (now >= start && now < end) return ev;
  }
  return null;
}

function normalizeChatMessages(messages, currentUserId) {
  if (!Array.isArray(messages)) return [];
  const uid = currentUserId != null ? String(currentUserId) : null;
  return messages.map((m) => {
    if (uid != null && m.authorId != null) {
      return { ...m, isOwn: String(m.authorId) === uid };
    }
    return { ...m, isOwn: !!m.isOwn };
  });
}

function shortenReplyPreview(text, maxLen = 50) {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  return t.length <= maxLen ? t : t.slice(0, maxLen) + '...';
}

function getClosestEvent(events) {
  if (!events.length) return null;
  const now = Date.now();
  let best = null;
  let bestDiff = Infinity;
  for (const ev of events) {
    const [hh, mm = 0] = (ev.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
    const startsAt = new Date();
    if (ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
      const [y, m, d] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
      startsAt.setFullYear(y, m - 1, d);
      startsAt.setHours(hh, mm, 0, 0);
    } else {
      startsAt.setHours(hh, mm, 0, 0);
      if (startsAt.getTime() < now) startsAt.setDate(startsAt.getDate() + 1);
    }
    const diff = startsAt.getTime() - now;
    if (diff >= 0 && diff < bestDiff) {
      bestDiff = diff;
      best = { ...ev, startsAt };
    }
  }
  if (best) return best;
  let pastBest = null;
  let pastBestDiff = -Infinity;
  for (const ev of events) {
    const [hh, mm = 0] = (ev.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
    const startsAt = new Date();
    if (ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
      const [y, m, d] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
      startsAt.setFullYear(y, m - 1, d);
      startsAt.setHours(hh, mm, 0, 0);
    } else {
      startsAt.setHours(hh, mm, 0, 0);
      if (startsAt.getTime() < now) startsAt.setDate(startsAt.getDate() + 1);
    }
    const diff = startsAt.getTime() - now;
    if (diff < 0 && diff > pastBestDiff) {
      pastBestDiff = diff;
      pastBest = { ...ev, startsAt };
    }
  }
  return pastBest;
}

export default function ChatView() {
  const { user } = useAuth();
  const { currentTrack } = useMusicPlayer();
  const [events, setEvents] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [lastSentMessage, setLastSentMessage] = useState('');
  const [slashResponses, setSlashResponses] = useState([]);
  const [mutedAuthorKeys, setMutedAuthorKeys] = useState(() => {
    try {
      const raw = localStorage.getItem('chatMutedAuthors');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const chatInputRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (!user) {
      window.location.href = '/';
      return;
    }
  }, [user]);

  const handleClose = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleToggleMuteAuthor = useCallback((authorKey) => {
    if (!authorKey) return;
    setMutedAuthorKeys((prev) => {
      const next = prev.includes(authorKey) ? prev.filter((k) => k !== authorKey) : [...prev, authorKey];
      try {
        localStorage.setItem('chatMutedAuthors', JSON.stringify(next));
      } catch (e) {
        console.warn(e);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (editingMessageId != null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingMessageId]);

  useEffect(() => {
    if (user && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [user]);

  // Load events for countdown and slash commands
  useEffect(() => {
    if (!user) return;
    const fetchEvents = () => {
      fetch(`${API}/api/events`)
        .then((res) => res.json())
        .then((data) => setEvents(Array.isArray(data) ? data : []))
        .catch(() => setEvents([]));
    };
    fetchEvents();
    const interval = setInterval(fetchEvents, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const upcomingEvents = useMemo(() => {
    const upcoming = events
      .filter(isEventUpcoming)
      .map((ev) => ({ ...ev, startsAt: getEventStartTime(ev) }));
    return upcoming.slice().sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  }, [events]);

  const nowLiveEvent = useMemo(() => getNowLiveEvent(events), [events]);
  const closestEvent = useMemo(() => getClosestEvent(events), [events]);
  const nextEvent = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  const nextAfterEvent = upcomingEvents.length > 1 ? upcomingEvents[1] : null;

  const fetchChat = useCallback(() => {
    const username = user?.displayName || user?.username || '';
    const url = username ? `${API}/api/chat?username=${encodeURIComponent(username)}` : `${API}/api/chat`;
    fetch(url)
      .then((res) => parseJsonResponse(res))
      .then((data) => setChatMessages(normalizeChatMessages(data.messages || [], user?.id)))
      .catch((err) => {
        console.error(err);
        setChatMessages([]);
      });
  }, [user?.id, user?.displayName, user?.username]);

  const kuiskaaCandidates = useMemo(() => buildKuiskaaCandidates(), []);

  const sendPresence = useCallback(() => {
    if (!user?.id || !user?.username) return;
    fetch(`${API}/api/chat/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        username: user.username,
        displayName: user.displayName || user.username,
      }),
    }).catch(() => {});
  }, [user?.id, user?.username, user?.displayName]);

  // When user enters Lobby Chat, ask backend to send pulinabotti welcome whisper (once per user)
  const lobbyEnterSentForRef = useRef(null);
  const lobbyUserId = user?.id ?? null;
  const lobbyDisplayName = user?.displayName ?? '';
  const lobbyUsername = user?.username ?? '';

  useEffect(() => {
    const username = (lobbyDisplayName || lobbyUsername || '').trim();
    if (!username || lobbyUserId == null) return;
    const key = `${lobbyUserId}:${username}`;
    if (lobbyEnterSentForRef.current === key) return;
    lobbyEnterSentForRef.current = key;
    fetch(`${API}/api/chat/lobby-enter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    }).catch(() => {});
  }, [lobbyUserId, lobbyDisplayName, lobbyUsername]);

  useEffect(() => {
    if (!user) return;
    sendPresence();
    const presenceInterval = setInterval(sendPresence, 30000);
    fetchChat();
    const chatInterval = setInterval(fetchChat, 5000);
    return () => {
      clearInterval(presenceInterval);
      clearInterval(chatInterval);
    };
  }, [user, fetchChat, sendPresence]);

  const handleSendChat = useCallback((e) => {
    e.preventDefault();
    const raw = (chatInput || '').trim();
    if (!raw || !user) return;
    setLastSentMessage(raw);
    const parsed = parseSlashCommand(raw);
    if (parsed) {
      setChatInput('');
      setReplyingToMessage(null);
      if (parsed.cmd === '/putsaa') {
        setSlashResponses([]);
        return;
      }
      executeSlashCommand(parsed.cmd, parsed.args, {
        user,
        currentTrack,
        upcomingEvents,
        formatEventDay,
        getEventStartTime,
      })
        .then((html) => {
          if (html == null) {
            if (parsed.cmd === '/kuiskaa') fetchChat();
            return;
          }
          setSlashResponses((prev) => [
            ...prev,
            {
              id: `slash-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              text: html,
              authorName: 'Pulina',
              isOwn: false,
              isCommandResponse: true,
              createdAt: new Date().toISOString(),
            },
          ]);
        })
        .catch(() => {
          setSlashResponses((prev) => [
            ...prev,
            {
              id: `slash-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              text: 'Komennon suoritus epäonnistui.',
              authorName: 'Pulina',
              isOwn: false,
              isCommandResponse: true,
              createdAt: new Date().toISOString(),
            },
          ]);
        });
      return;
    }
    const text = raw.slice(0, 500);
    const userMsg = {
      text,
      authorName: user.displayName || user.username,
      authorId: user.id,
      ...(replyingToMessage && {
        replyTo: {
          messageId: replyingToMessage.id,
          textPreview: shortenReplyPreview(replyingToMessage.text),
        },
      }),
    };
    // Optimistically show the user's message immediately
    const optimisticMessage = {
      ...userMsg,
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isOwn: true,
      createdAt: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, optimisticMessage]);
    setChatInput('');
    setReplyingToMessage(null);
    fetch(`${API}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: [userMsg] }),
    })
      .then((res) => parseJsonResponse(res))
      .then((data) => setChatMessages(normalizeChatMessages(data.messages || [], user.id)))
      .catch((err) => console.error(err));
  }, [chatInput, replyingToMessage, user, currentTrack, upcomingEvents, fetchChat]);

  const handleDeleteOwnMessage = useCallback((id) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän viestin?')) return;
    if (editingMessageId === id) {
      setEditingMessageId(null);
      setEditingDraft('');
    }
    const next = chatMessages.filter((m) => m.id !== id);
    setChatMessages(normalizeChatMessages(next, user?.id));
    fetch(`${API}/api/chat`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(console.error);
  }, [chatMessages, editingMessageId, user?.id]);

  const handleStartEditOwnMessage = useCallback((msg) => {
    setEditingMessageId(msg.id);
    setEditingDraft(msg.text);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingMessageId == null) return;
    const text = editingDraft.trim().slice(0, 500);
    const updatedAt = new Date().toISOString();
    const next = chatMessages.map((m) =>
      m.id === editingMessageId ? { ...m, text: text || m.text, updatedAt } : m
    );
    setChatMessages(normalizeChatMessages(next, user?.id));
    setEditingMessageId(null);
    setEditingDraft('');
    fetch(`${API}/api/chat`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(console.error);
  }, [editingMessageId, editingDraft, chatMessages, user?.id]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingDraft('');
  }, []);

  const handleReplyToMessage = useCallback((msg) => {
    setReplyingToMessage({ id: msg.id, text: msg.text || '', authorName: msg.authorName });
    chatInputRef.current?.focus();
  }, []);

  const handleInsertToInput = useCallback((text) => {
    setChatInput(String(text ?? ''));
    chatInputRef.current?.focus();
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingToMessage(null);
  }, []);

  const handleEditInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSaveEdit();
    }
  }, [handleCancelEdit, handleSaveEdit]);

  return (
    <div className="App-block Chat-view">
      <div className="Chat-window">
        <div className="Chat-topbar-module">
          <ChatTopBar
            onBack={handleClose}
            nowLiveEvent={nowLiveEvent}
            closestEvent={closestEvent}
            nextEvent={nextEvent}
            nextAfterEvent={nextAfterEvent}
          />
        </div>
        <div className="Chat-messages-module">
          <ChatMessages
            messages={[...chatMessages, ...slashResponses]}
            editingMessageId={editingMessageId}
            editingDraft={editingDraft}
            setEditingDraft={setEditingDraft}
            editInputRef={editInputRef}
            onStartEdit={handleStartEditOwnMessage}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onDeleteMessage={handleDeleteOwnMessage}
            onReply={handleReplyToMessage}
            onEditInputKeyDown={handleEditInputKeyDown}
            currentUserDisplayName={user?.displayName || user?.username}
            profileRedirectPath="/chat/"
            mutedAuthorKeys={mutedAuthorKeys}
            onToggleMuteAuthor={handleToggleMuteAuthor}
            onInsertToInput={handleInsertToInput}
          />
        </div>
        <ChatInput
          inputRef={chatInputRef}
          value={chatInput}
          onChangeValue={setChatInput}
          onSubmit={handleSendChat}
          lastSentMessage={lastSentMessage}
          placeholder="Kirjoita viesti..."
          maxLength={500}
          replyingTo={replyingToMessage}
          onCancelReply={handleCancelReply}
          mentionCandidates={Array.from(
            new Set([
              'pulinabotti',
              ...(chatMessages || []).map((m) => m.authorName).filter(Boolean),
            ])
          )}
          kuiskaaCandidates={kuiskaaCandidates}
          currentUser={user}
          leftSlot={<ChatInlinePlayer />}
        />
      </div>
    </div>
  );
}
